import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, AlertTriangle } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { toastPermissionDenied, toastErrorOnce } from '../utils/permissionToast';
import { useAuth } from '../contexts/AuthContext';
import SearchableSelect from '../components/SearchableSelect';
import { addDays, calcBusinessDays, companyToday } from '../utils/dateUtils';

const EMPTY_FORM = {
  leave_type_id: '',
  start_date: '',
  end_date: '',
  reason: '',
};

// Doubles as the edit form for a still-pending request (route `leaves/:id/edit`).
// Create and edit are the same five inputs, so they share one component rather than
// being kept in sync across two.
const AddLeave = () => {
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balance, setBalance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  // `hasPermission` / `user` are fresh identities on every AuthProvider render, so the
  // effect below can re-run. Prefill once PER ID or it clobbers in-progress edits -
  // keyed on the id rather than a boolean, because react-router reuses this component
  // instance when only :id changes, and a boolean would leave the previous request's
  // values in the form while the id (and so the PUT target) had moved on.
  const prefilledFor = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!hasPermission('leaves', 'create_own')) {
      toastPermissionDenied('You do not have permission to submit leave requests');
      navigate('/leaves');
      return;
    }
    if (user && !user.employee_id) {
      toastErrorOnce('Please complete onboarding before applying for leave.');
      navigate('/onboarding');
      return;
    }
    api.get('/api/leaves/types')
      .then(res => setLeaveTypes(res.data.data || []))
      .catch(() => toast.error('Failed to load leave types'));

    if (!isEdit) {
      // Leaving edit mode for /leaves/new reuses this instance: start the new request
      // blank rather than on the previous request's values, and forget the prefill so
      // coming back to that request's edit route loads it again. Clear the loading state
      // too: a prefill still in flight is dropped when it lands, so it never will.
      if (prefilledFor.current !== null) {
        prefilledFor.current = null;
        setForm(EMPTY_FORM);
        setLoading(false);
      }
      return;
    }
    if (prefilledFor.current === id) return;
    prefilledFor.current = id;
    setLoading(true);
    api.get(`/api/leaves/${id}`)
      .then(res => {
        // The route moved on to another request before this one answered: its values
        // must not land in that request's form (the ref above already stops
        // StrictMode's second run from fetching at all).
        if (prefilledFor.current !== id) return;
        const request = res.data.data;
        // Someone can deep-link or hit Back to this route after the request stopped being
        // editable. The server refuses either way (409/403) — this only spares them a form
        // that is guaranteed to fail.
        if (request.status !== 'pending') {
          toast.error('Only pending requests can be edited');
          navigate(`/leaves/${id}`);
          return;
        }
        if (String(user?.employee_id) !== String(request.employee_id)) {
          toast.error('You can only edit your own leave requests');
          navigate(`/leaves/${id}`);
          return;
        }
        setForm({
          leave_type_id: String(request.leave_type_id),
          start_date: request.start_date,
          end_date: request.end_date,
          reason: request.reason || '',
        });
        setLoading(false);
      })
      .catch(err => {
        if (prefilledFor.current !== id) return;
        toast.error(err.response?.data?.message || 'Failed to load leave request');
        navigate('/leaves');
      });
  }, [hasPermission, user, navigate, id, isEdit]);

  useEffect(() => {
    if (!form.leave_type_id) { setBalance(null); return; }
    api.get('/api/leaves/balances/me')
      .then(res => {
        const balances = res.data.data || [];
        const found = balances.find(b => String(b.leave_type_id) === String(form.leave_type_id));
        setBalance(found || null);
      })
      .catch(() => setBalance(null));
  }, [form.leave_type_id]);

  const selectedType = leaveTypes.find(t => String(t.id) === String(form.leave_type_id));

  // The earliest start the server accepts for this type (validateRequest in
  // leaveService.js): no more than allow_backdate_days before today and, with a notice
  // period, no sooner than min_notice_days after it. "Today" is the company's day on both
  // sides, so a browser abroad offers exactly the days the server takes.
  const earliestStart = selectedType
    ? addDays(companyToday(), selectedType.min_notice_days > 0
      ? selectedType.min_notice_days
      : -(selectedType.allow_backdate_days || 0))
    : undefined;

  const calcDays = () => {
    if (!form.start_date || !form.end_date) return null;
    // Both are 'YYYY-MM-DD', which order as text in calendar order.
    if (form.end_date < form.start_date) return null;
    return calcBusinessDays(form.start_date, form.end_date);
  };

  const requestedDays = calcDays();
  const remaining = balance ? balance.total_quota - balance.used_days : null;
  const showWarning =
    selectedType?.is_balance_tracked &&
    balance !== null &&
    requestedDays !== null &&
    requestedDays > remaining;

  const handleChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.leave_type_id || !form.start_date || !form.end_date || !form.reason.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.end_date < form.start_date) {
      toast.error('End date cannot be before start date');
      return;
    }
    setSubmitting(true);
    try {
      const res = isEdit
        ? await api.put(`/api/leaves/${id}`, form)
        : await api.post('/api/leaves', form);
      if (res.data.warning) {
        toast.success(isEdit ? 'Leave request updated' : 'Request submitted');
        toast(res.data.warning, { icon: '⚠️', duration: 6000 });
      } else {
        toast.success(isEdit ? 'Leave request updated' : 'Leave request submitted successfully');
      }
      navigate(isEdit ? `/leaves/${id}` : '/leaves');
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
        (isEdit ? 'Failed to update leave request' : 'Failed to submit leave request')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <Link
          to={isEdit ? `/leaves/${id}` : '/leaves'}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Link>
        <div className="flex items-center space-x-3">
          <Calendar className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Leave Request' : 'New Leave Request'}</h1>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Leave Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Leave Type <span className="text-danger-600">*</span>
            </label>
            <SearchableSelect
              data-testid="add-leave-type"
              value={form.leave_type_id}
              onChange={val => setForm(prev => ({ ...prev, leave_type_id: val }))}
              options={leaveTypes.map(t => ({ value: String(t.id), label: t.name }))}
              placeholder="Select leave type"
            />
          </div>

          {/* Balance info */}
          {selectedType?.is_balance_tracked && balance && (
            <div className="bg-gray-50 rounded-md px-4 py-3 text-sm text-gray-700">
              Balance: <span className="font-medium">{balance.used_days}</span> used
              {' / '}
              <span className="font-medium">{balance.total_quota}</span> total
              {' — '}
              <span className="font-medium">{Math.max(0, balance.total_quota - balance.used_days)}</span> remaining
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-danger-600">*</span>
              </label>
              <input
                type="date"
                name="start_date"
                data-testid="add-leave-field-start-date"
                value={form.start_date}
                min={earliestStart}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date <span className="text-danger-600">*</span>
              </label>
              <input
                type="date"
                name="end_date"
                data-testid="add-leave-field-end-date"
                value={form.end_date}
                min={form.start_date || earliestStart}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          {/* Business days preview */}
          {requestedDays !== null && (
            <p className="text-sm text-gray-500" data-testid="add-leave-days-preview">
              Requesting <span className="font-medium text-gray-800">{requestedDays}</span> business day{requestedDays !== 1 ? 's' : ''}
            </p>
          )}

          {/* Balance warning */}
          {showWarning && (
            <div className="flex items-start gap-2 bg-warning-50 border border-warning-200 rounded-md px-4 py-3 text-sm text-warning-800" data-testid="add-leave-balance-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                You are requesting <strong>{requestedDays}</strong> day{requestedDays !== 1 ? 's' : ''} but only have <strong>{Math.max(0, remaining)}</strong> remaining.
                The request will still be submitted for HR review.
              </span>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-danger-600">*</span>
            </label>
            <textarea
              name="reason"
              data-testid="add-leave-field-reason"
              value={form.reason}
              onChange={handleChange}
              rows={4}
              placeholder="Please provide a reason for your leave request…"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-none"
              required
            />
          </div>

          <button
            type="submit"
            data-testid="add-leave-submit"
            disabled={submitting}
            className="w-full inline-flex justify-center items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors disabled:opacity-60"
          >
            {isEdit
              ? (submitting ? 'Saving…' : 'Save Changes')
              : (submitting ? 'Submitting…' : 'Submit Request')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddLeave;
